import apiClient from './client';
import type {
  JlrAuthConfirmResult,
  JlrAuthStartResult,
  JlrConversion,
  JlrPlanGroup,
  JlrUploadPlan,
  ThirdPartyPlugin,
  ThirdPartyUploadTask,
  ThirdPartyUploadTaskList,
  ThirdPartyUploadTriggerResult,
} from '../types';

/**
 * 第三方插件目录（只读）。接入参数与凭据由插件内置，接口不返回任何配置字段。
 * @param availableOnly 仅返回可选用的插件，供项目表单下拉使用
 */
export const getThirdPartyPlugins = async (
  availableOnly?: boolean
): Promise<ThirdPartyPlugin[]> => {
  const { data } = await apiClient.get<ThirdPartyPlugin[]>('/third-party/plugins/', {
    params: availableOnly ? { available_only: true } : undefined,
  });
  return data;
};

export interface TriggerUploadOptions {
  /** 是否重传已成功的文件 */
  includeSucceeded?: boolean;
  /**
   * 第三方制品库目标目录（Feature 009）。仅插件 requires_target_dir 为真时必填，
   * 其他插件传了也会被后端忽略。
   */
  targetDir?: string;
  /**
   * 交互认证会话（JLR SharePoint）。由 `startJlrAuth` 返回，仅
   * `requires_interactive_auth` 的插件需要。
   */
  authSession?: string;
  /** 父目录链接（JLR SharePoint）。留空则用后端配置的默认值 */
  parentUrl?: string;
  /** 父目录下的文件夹，可多级（JLR SharePoint 必填） */
  folder?: string;
  /**
   * 用户已确认覆盖目标文件夹里的同名文件。
   * 默认不传：后端预检到重名会返回 409 `files_exist`，前端弹窗询问后带 true 重试。
   */
  overwrite?: boolean;
  /** 用户选择只上传不同名的文件（与 overwrite 互斥） */
  skipExisting?: boolean;
  /**
   * 多目录分组（JLR SharePoint）。传空数组 = 全部文件放父链接根目录；
   * 不传则沿用旧的 `folder` 单目录契约。
   */
  groups?: JlrPlanGroup[];
  /** 勾选转 PDF 的飞书文档 */
  conversions?: JlrConversion[];
}

/** 目标文件夹里已存在的同名文件（409 files_exist 的附带信息） */
export interface ExistingFileConflict {
  name: string;
  label: string;
  local_size: number;
  remote_size: number;
  /** 两侧大小一致 / 不一致 / 任一侧未知（null） */
  same_size: boolean | null;
  /** 多目录上传时该文件所属的目录（根目录为空串），旧单目录后端不返回 */
  folder?: string;
  folder_path?: string;
  /** 计划里的稳定文件键，用于在冲突重试时保持分组/转换不变 */
  file_key?: string;
}

/** 409 files_exist 的完整载荷：冲突清单 + 本次一共要传几个文件 */
export interface ExistingFilesCheck {
  conflicts: ExistingFileConflict[];
  total: number;
  /** 后端的原始提示，冲突清单缺失时兜底展示 */
  message?: string;
}

/**
 * 启动 JLR SharePoint 的交互式认证。
 *
 * 该站点强制 MFA：后端会起一个无头浏览器填账号密码，阻塞到登录页出现
 * Authenticator 的号码匹配数字再返回。**密码只在这一请求里存在，后端不落库**。
 * 用户在手机上输入 `sign_number` 并批准后，带 `session_id` 调 `triggerUpload`。
 */
export const startJlrAuth = async (
  releaseId: number,
  payload: { username: string; password: string }
): Promise<JlrAuthStartResult> => {
  const { data } = await apiClient.post<JlrAuthStartResult>(
    `/third-party/releases/${releaseId}/auth/start/`,
    payload,
    // 后端要等无头浏览器走完账号→密码→号码页，实测 15~40 秒
    { timeout: 120000 }
  );
  return data;
};

/**
 * 用户在 Authenticator 里批准后，确认登录已生效。
 *
 * 与 `triggerUpload` 里的确认是同一件事，单独成端点是为了让第一步弹窗拿到
 * 「已登录」的明确结果再进入第二步（选路径/转换文档）。68 最长阻塞 60 秒，
 * 所以超时给到 90 秒以上。
 */
export const confirmJlrAuth = async (
  releaseId: number,
  sessionId: string
): Promise<JlrAuthConfirmResult> => {
  const { data } = await apiClient.post<JlrAuthConfirmResult>(
    `/third-party/releases/${releaseId}/auth/confirm/`,
    { auth_session: sessionId },
    { timeout: 90000 }
  );
  return data;
};

/**
 * 第二步弹窗的只读预览：可参与分组的文件 + 可转 PDF 的飞书链接。
 * 不建任务、不发起转换，失败也不影响上传入口。
 */
export const getJlrUploadPlan = async (releaseId: number): Promise<JlrUploadPlan> => {
  const { data } = await apiClient.get<JlrUploadPlan>(
    `/third-party/releases/${releaseId}/upload-plan/`
  );
  return data;
};

/**
 * 触发产物包下全部文件的第三方上传。后端立即返回 202，实际推送在后台进行。
 * 重复触发是幂等的：已在队列中的文件会被跳过（created_count 为 0）。
 */
export const triggerUpload = async (
  releaseId: number,
  opts: TriggerUploadOptions = {}
): Promise<ThirdPartyUploadTriggerResult> => {
  const body: Record<string, unknown> = { include_succeeded: opts.includeSucceeded ?? false };
  // 传了才带，且**空串也带**：德赛的可选目录允许显式清空（回到分享根目录）；
  // 完全不传（undefined）表示沿用发布记录上的已有值。
  if (opts.targetDir !== undefined) body.target_dir = opts.targetDir;
  // 交互认证型插件的三项：只在确实有值时带，避免给其他插件塞空字段
  if (opts.authSession) body.auth_session = opts.authSession;
  if (opts.parentUrl !== undefined) body.parent_url = opts.parentUrl;
  if (opts.folder !== undefined) body.folder = opts.folder;
  // groups 为空数组也要带：空数组表示「全部放父链接根目录」，与不传（旧单目录契约）语义不同
  if (opts.groups !== undefined) body.groups = opts.groups;
  if (opts.conversions && opts.conversions.length > 0) body.conversions = opts.conversions;
  if (opts.overwrite) body.overwrite = true;
  if (opts.skipExisting) body.skip_existing = true;
  const { data } = await apiClient.post<ThirdPartyUploadTriggerResult>(
    `/third-party/releases/${releaseId}/upload/`,
    body
  );
  return data;
};

/** 从 triggerUpload 的异常中取出 409 附带的同名文件冲突清单；不是该错误时返回 null */
export const getExistingFilesFromError = (e: unknown): ExistingFilesCheck | null => {
  const resp = (e as {
    response?: {
      status?: number;
      data?: { error?: string; message?: string; conflicts?: ExistingFileConflict[]; total?: number };
    };
  }).response;
  const data = resp?.data;
  // 只认契约里的 error 码，不强求 conflicts 一定在：只要后端说是「同名文件」，
  // 就必须把覆盖/跳过/取消的选择权交给用户，不能因为少个字段退化成一条报错提示。
  if (resp?.status !== 409 || data?.error !== 'files_exist') return null;
  const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
  return {
    conflicts,
    total: Number(data.total ?? conflicts.length),
    message: data.message,
  };
};

/** 查询产物包的逐文件上传状态与聚合状态，供详情页轮询 */
export const getUploadTasks = async (
  releaseId: number
): Promise<ThirdPartyUploadTaskList> => {
  const { data } = await apiClient.get<ThirdPartyUploadTaskList>(
    `/third-party/releases/${releaseId}/tasks/`
  );
  return data;
};

/** 重试单个失败的文件上传。仅 failed 状态可重试，其他状态后端返回 invalid_state */
export const retryTask = async (taskId: number): Promise<ThirdPartyUploadTask> => {
  const { data } = await apiClient.post<ThirdPartyUploadTask>(
    `/third-party/tasks/${taskId}/retry/`
  );
  return data;
};
